import React, { useEffect, useState } from 'react';
import { TextField, TextareaField, ToggleField } from '../../../components/orizon-ui/FormField';
import { useToast } from '../../../components/orizon-ui/Toast';
import {
  colataCrea, colataAggiorna, colataOpzioni, colataSalvaElencoZona, mapColataError,
} from '../../../lib/services/colataService';
import FormModal, { useFormSalva } from '../../../components/colata/FormModal';
import ElencoNomi, { righeDaRecord, righeDaSalvare, validaRighe } from '../../../components/colata/ElencoNomi';
import SezioneConfig, { colonnaAttivo } from './SezioneConfig';
import { vuoto } from '../utils';

// Istantanea dei valori del form, per sapere se ci sono modifiche non salvate
const istantanea = (form, impianti, linee) => JSON.stringify({
  form,
  impianti: impianti.map((r) => [r.id, r.nome, r.attivo]),
  linee: linee.map((r) => [r.id, r.nome, r.attivo]),
});

// Applica gli esiti del salvataggio alle righe: le righe riuscite diventano "salvate", le altre mostrano l'errore
const applicaEsiti = (righe, esiti) => {
  const errori = {};
  const salvate = righeDaSalvare(righe).map((r, i) => {
    const e = esiti[i];
    if (e?.ok) {
      return { ...r, id: e.id, originale: e.salvato, nuova: false };
    }
    if (e) errori[r.chiave] = e.errore;
    return r;
  });
  return { righe: salvate, errori };
};

const FormZona = ({ record, onClose, onSaved }) => {
  const toast = useToast();
  const [idZona, setIdZona] = useState(record?.id ?? null); // dopo un salvataggio parziale la zona esiste già
  const modifica = Boolean(idZona);

  const [form, setForm] = useState({
    codice: record?.codice ?? '',
    nome: record?.nome ?? '',
    descrizione: record?.descrizione ?? '',
    attivo: record?.attivo ?? true,
  });
  const [impianti, setImpianti] = useState([]);
  const [linee, setLinee] = useState([]);
  const [erroriImpianti, setErroriImpianti] = useState({});
  const [erroriLinee, setErroriLinee] = useState({});
  const [caricamento, setCaricamento] = useState(Boolean(record));
  const [salvataParzialmente, setSalvataParzialmente] = useState(false);
  // istantanea iniziale: subito per una zona nuova, dopo il caricamento degli elenchi per una esistente
  const [base, setBase] = useState(record ? null : istantanea(
    { codice: '', nome: '', descrizione: '', attivo: true }, [], [],
  ));

  const { salvando, errori, impostaErrori, pulisci, salva } = useFormSalva();

  // impianti e linee già presenti (anche disattivati)
  useEffect(() => {
    if (!record) return undefined;
    let annullato = false;
    Promise.all([
      colataOpzioni('impianti', { zona_id: record.id }),
      colataOpzioni('linee', { zona_id: record.id }),
    ])
      .then(([imp, lin]) => {
        if (annullato) return;
        const ri = righeDaRecord(imp);
        const rl = righeDaRecord(lin);
        setImpianti(ri);
        setLinee(rl);
        setBase(istantanea(form, ri, rl));
        setCaricamento(false);
      })
      .catch((e) => {
        if (annullato) return;
        toast.error(mapColataError(e).message);
        setCaricamento(false);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sporco = base !== null && istantanea(form, impianti, linee) !== base;

  const imposta = (campo) => (valore) => {
    setForm((prec) => ({ ...prec, [campo]: valore }));
    pulisci(campo);
  };

  const cambiaImpianti = (righe) => { setImpianti(righe); setErroriImpianti({}); };
  const cambiaLinee = (righe) => { setLinee(righe); setErroriLinee({}); };

  const invia = async () => {
    const nuovi = {};
    if (vuoto(form.codice)) nuovi.codice = 'Obbligatorio';
    if (vuoto(form.nome)) nuovi.nome = 'Obbligatorio';
    impostaErrori(nuovi);

    const errImp = validaRighe(impianti);
    const errLin = validaRighe(linee);
    setErroriImpianti(errImp);
    setErroriLinee(errLin);
    if (Object.keys(nuovi).length || Object.keys(errImp).length || Object.keys(errLin).length) return;

    const payload = {
      codice: form.codice.trim(),
      nome: form.nome.trim(),
      descrizione: vuoto(form.descrizione) ? null : form.descrizione.trim(),
      attivo: form.attivo,
    };

    // 1) la zona
    let zona = null;
    const ok = await salva(
      async () => {
        zona = idZona ? await colataAggiorna('zone', idZona, payload) : await colataCrea('zone', payload);
        return zona;
      },
      modifica ? 'Zona aggiornata.' : 'Zona creata.',
    );
    if (!ok || !zona) return;
    setIdZona(zona.id);
    setSalvataParzialmente(true);

    // 2) impianti e linee, una riga per volta: un errore su una riga non ferma le altre
    const daSalvareImp = righeDaSalvare(impianti);
    const daSalvareLin = righeDaSalvare(linee);
    const esitiImp = await colataSalvaElencoZona('impianti', zona.id, daSalvareImp);
    const esitiLin = await colataSalvaElencoZona('linee', zona.id, daSalvareLin);

    const ri = applicaEsiti(impianti, esitiImp);
    const rl = applicaEsiti(linee, esitiLin);
    setImpianti(ri.righe);
    setLinee(rl.righe);
    setErroriImpianti(ri.errori);
    setErroriLinee(rl.errori);

    const falliti = Object.keys(ri.errori).length + Object.keys(rl.errori).length;
    if (falliti > 0) {
      // la zona è salvata: la forma resta aperta per correggere le righe in errore
      setBase(istantanea(form, ri.righe.filter((r) => !ri.errori[r.chiave]), rl.righe.filter((r) => !rl.errori[r.chiave])));
      toast.error(`${falliti} ${falliti === 1 ? 'elemento non salvato' : 'elementi non salvati'}: controlla le righe in rosso.`);
      return;
    }
    onSaved();
  };

  // se la zona è già stata salvata (anche in parte), chiudendo si aggiorna comunque la lista
  const chiudi = () => (salvataParzialmente ? onSaved() : onClose());

  return (
    <FormModal
      titolo={modifica ? 'Modifica zona' : 'Nuova zona'}
      etichetta="Configurazione · Zone"
      larghezza="lg"
      sporco={sporco}
      salvando={salvando}
      onClose={chiudi}
      onSubmit={invia}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <TextField label="Codice" name="codice" value={form.codice} onChange={imposta('codice')} obbligatorio errore={errori.codice} mono autoFocus placeholder="es. CCA" />
        <TextField label="Nome" name="nome" value={form.nome} onChange={imposta('nome')} obbligatorio errore={errori.nome} placeholder="es. Colata continua A" />
      </div>
      <TextareaField label="Descrizione" name="descrizione" value={form.descrizione} onChange={imposta('descrizione')} errore={errori.descrizione} />

      <ElencoNomi
        titolo="Impianti"
        icona="Factory"
        descrizione="Gli impianti di questa zona, uno per riga (es. TRASFERITORE, BANCO OSCILLANTE). Si scelgono negli interventi e nei componenti."
        segnaposto="Nome impianto"
        aggiungiLabel="Aggiungi impianto"
        vuotoLabel="Nessun impianto. Aggiungine uno con il pulsante +."
        righe={impianti}
        onChange={cambiaImpianti}
        errori={erroriImpianti}
        disabilitato={caricamento || salvando}
      />

      <ElencoNomi
        titolo="Linee (facoltativo)"
        icona="GitBranch"
        descrizione="Solo se questa zona ha delle linee (es. L1, L2, L3). Negli interventi comparirà il campo Linea."
        segnaposto="Nome linea"
        aggiungiLabel="Aggiungi linea"
        vuotoLabel="Nessuna linea: il campo Linea non comparirà negli interventi di questa zona."
        righe={linee}
        onChange={cambiaLinee}
        errori={erroriLinee}
        disabilitato={caricamento || salvando}
      />

      <ToggleField label="Attiva" sublabel={form.attivo ? 'Selezionabile negli interventi' : 'Non selezionabile'} checked={form.attivo} onChange={imposta('attivo')} />
    </FormModal>
  );
};

const COLONNE = [
  { key: 'codice', label: 'Codice', width: 140, render: (r) => <span className="font-mono text-[12px] font-bold text-foreground/80">{r.codice}</span> },
  { key: 'nome', label: 'Nome', width: 240, render: (r) => <span className="text-[13px] font-black uppercase tracking-tight text-foreground/80">{r.nome}</span> },
  { key: 'n_impianti', label: 'Impianti', width: 100, align: 'center', render: (r) => <span className="text-[13px] font-black tabular-nums text-foreground/80">{r.n_impianti}</span> },
  { key: 'n_linee', label: 'Linee', width: 90, align: 'center', render: (r) => <span className="text-[13px] font-black tabular-nums text-foreground/80">{r.n_linee}</span> },
  { key: 'descrizione', label: 'Descrizione', width: 300, render: (r) => <span className="text-[12px] font-medium text-muted-foreground">{r.descrizione || '---'}</span> },
  colonnaAttivo,
];

const TabZone = () => (
  <SezioneConfig
    entita="zone"
    titolo="Zone"
    descrizione="Zone con i loro impianti e, se servono, le linee"
    nuovoLabel="Nuova zona"
    colonne={COLONNE}
    ordineIniziale={{ key: 'codice', direction: 'asc' }}
    FormComponent={FormZona}
    labelRiga={(r) => `${r.codice} — ${r.nome}`}
    messaggioVuoto="Nessuna zona configurata"
  />
);

export default TabZone;
